import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  effect,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';

interface NavItem {
  label: string;
  icon?: string;
  route?: string;
  items?: NavItem[];
}

const EMPLOYEE_PORTAL_PANEL_LABEL = 'My Portal';
const DASHBOARDS_PANEL_LABEL = 'Dashboards';

const EMPLOYEE_PANEL_LABELS = new Set(['My Menu', 'My Tasks', EMPLOYEE_PORTAL_PANEL_LABEL]);

const DASHBOARD_FEATURE_ROUTES = new Set(['/features/payroll', '/features/hr/dashboard']);

const PANEL_ICONS: Record<string, string> = {
  [DASHBOARDS_PANEL_LABEL]: 'pi pi-fw pi-th-large',
  [EMPLOYEE_PORTAL_PANEL_LABEL]: 'pi pi-fw pi-id-card',
  'My Menu': 'pi pi-fw pi-th-large',
  'My Tasks': 'pi pi-fw pi-check-square',
  Payroll: 'pi pi-fw pi-wallet',
  Finance: 'pi pi-fw pi-book',
  HR: 'pi pi-fw pi-users',
  Staffing: 'pi pi-fw pi-briefcase',
  Organization: 'pi pi-fw pi-building',
  Admin: 'pi pi-fw pi-shield',
};

@Component({
  selector: 'app-sidebar',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnChanges {
  @Input() collapsed = false;
  @Input() mobileOpen = false;

  navMenu: NavItem[] = [];
  searchQuery = '';
  openFlyoutPanel: string | null = null;
  flyoutTop = 0;
  flyoutLeft = 0;
  flyoutArrowTop = 20;
  readonly expandedPanels = new Set<string>();
  readonly expandedGroups = new Set<string>();

  readonly userProfileStore = inject(UserProfileStore);

  enableMenuSearch(event: FocusEvent): void {
    (event.target as HTMLInputElement | null)?.removeAttribute('readonly');
  }

  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.closeCollapsedFlyout();
        this.syncExpandedFromRoute();
        this.cdr.markForCheck();
      });

    effect(() => {
      const roleFeatures = this.userProfileStore.roleFeatures();
      const roleName = this.userProfileStore.roleName();
      if (roleName.toLowerCase() === 'employee') {
        this.buildEmployeeMenu();
      } else {
        this.buildMenu(roleFeatures);
      }
      this.syncExpandedFromRoute();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['collapsed'] && !this.collapsed) {
      this.closeCollapsedFlyout();
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeCollapsedFlyout();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.closeCollapsedFlyout();
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  get showSearch(): boolean {
    return this.countNavItems(this.navMenu) > 8;
  }

  get showNavControls(): boolean {
    return !this.collapsed && this.navMenu.length > 1;
  }

  get allSectionsExpanded(): boolean {
    if (this.searchQuery.trim()) {
      return true;
    }

    const panels = this.displayNavMenu;
    if (panels.length === 0) {
      return false;
    }

    for (const panel of panels) {
      if (!this.expandedPanels.has(panel.label)) {
        return false;
      }

      for (const item of panel.items ?? []) {
        if (item.items?.length && !this.expandedGroups.has(this.groupKey(panel.label, item.label))) {
          return false;
        }
      }
    }

    return true;
  }

  get displayNavMenu(): NavItem[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.navMenu;
    }
    return this.filterNavMenu(this.navMenu, query);
  }

  getPanelByLabel(label: string | null): NavItem | undefined {
    if (!label) {
      return undefined;
    }
    return this.navMenu.find((panel) => panel.label === label);
  }

  isPanelActive(panel: NavItem): boolean {
    const match = this.findBestRouteMatch(this.router.url.split('?')[0]);
    return match?.panel.label === panel.label;
  }

  toggleCollapsedFlyout(panelLabel: string, event: MouseEvent): void {
    event.stopPropagation();

    if (this.openFlyoutPanel === panelLabel) {
      this.closeCollapsedFlyout();
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    this.flyoutTop = rect.top;
    this.flyoutLeft = rect.right + 8;
    this.flyoutArrowTop = rect.height / 2;
    this.openFlyoutPanel = panelLabel;
    this.cdr.markForCheck();
  }

  closeCollapsedFlyout(): void {
    if (!this.openFlyoutPanel) {
      return;
    }
    this.openFlyoutPanel = null;
    this.cdr.markForCheck();
  }

  onFlyoutClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  onFlyoutLinkClick(): void {
    this.closeCollapsedFlyout();
  }

  togglePanel(label: string): void {
    if (this.expandedPanels.has(label)) {
      this.expandedPanels.delete(label);
      // Drop nested groups under this panel so reopening starts clean.
      for (const key of [...this.expandedGroups]) {
        if (key.startsWith(`${label}::`)) {
          this.expandedGroups.delete(key);
        }
      }
    } else {
      // Accordion: only one top-level module open at a time (keeps the rail light).
      this.expandedPanels.clear();
      this.expandedGroups.clear();
      this.expandedPanels.add(label);
    }
    this.cdr.markForCheck();
  }

  expandAllPanels(): void {
    for (const panel of this.displayNavMenu) {
      this.expandedPanels.add(panel.label);
      for (const item of panel.items ?? []) {
        if (item.items?.length) {
          this.expandedGroups.add(this.groupKey(panel.label, item.label));
        }
      }
    }
    this.cdr.markForCheck();
  }

  collapseAllPanels(): void {
    this.expandedPanels.clear();
    this.expandedGroups.clear();
    this.cdr.markForCheck();
  }

  toggleAllPanels(): void {
    if (this.allSectionsExpanded) {
      this.collapseAllPanels();
    } else {
      this.expandAllPanels();
    }
  }

  isPanelExpanded(label: string): boolean {
    if (this.searchQuery.trim()) {
      return true;
    }
    return this.expandedPanels.has(label);
  }

  toggleGroup(panelLabel: string, groupLabel: string): void {
    const key = this.groupKey(panelLabel, groupLabel);
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      // Accordion within the open module.
      for (const existing of [...this.expandedGroups]) {
        if (existing.startsWith(`${panelLabel}::`)) {
          this.expandedGroups.delete(existing);
        }
      }
      this.expandedGroups.add(key);
    }
    this.cdr.markForCheck();
  }

  isGroupExpanded(panelLabel: string, groupLabel: string): boolean {
    if (this.searchQuery.trim()) {
      return true;
    }
    return this.expandedGroups.has(this.groupKey(panelLabel, groupLabel));
  }

  onSearchChange(): void {
    this.cdr.markForCheck();
  }

  iconClass(icon?: string): string {
    return icon || 'pi pi-fw pi-file';
  }

  panelIcon(panel: NavItem): string {
    if (panel.icon) {
      return panel.icon;
    }
    return this.panelIconForLabel(panel.label);
  }

  formatPanelLabel(label: string): string {
    return label
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private panelIconForLabel(label: string): string {
    const normalized = label.trim().toLowerCase();
    for (const [key, icon] of Object.entries(PANEL_ICONS)) {
      if (key.toLowerCase() === normalized) {
        return icon;
      }
    }
    return 'pi pi-fw pi-folder';
  }

  private groupKey(panelLabel: string, groupLabel: string): string {
    return `${panelLabel}::${groupLabel}`;
  }

  private buildEmployeeMenu(): void {
    if (!this.userProfileStore.hasEmployeeSelfService()) {
      this.navMenu = [];
      return;
    }
    this.navMenu = [...this.employeePortalSections()];
  }

  private employeePortalSections(compact = false): NavItem[] {
    if (!this.userProfileStore.hasEmployeeSelfService()) {
      return [];
    }
    const menuItems: NavItem[] = [
      { label: 'My Dashboard', icon: 'pi pi-fw pi-home', route: '/me/dashboard' },
      { label: 'Biometrics', icon: 'pi pi-fw pi-id-card', route: '/me/biometrics' },
      { label: 'Daily Time Record', icon: 'pi pi-fw pi-calendar', route: '/me/dtr' },
      { label: 'My Payslips', icon: 'pi pi-fw pi-file-pdf', route: '/me/payslips' },
      { label: 'Leave Requests', icon: 'pi pi-fw pi-sun', route: '/me/leaves' },
      { label: 'Overtime Requests', icon: 'pi pi-fw pi-clock', route: '/me/overtime' },
      { label: 'Time Corrections', icon: 'pi pi-fw pi-pen-to-square', route: '/me/time-corrections' },
      { label: 'Loan Requests', icon: 'pi pi-fw pi-wallet', route: '/me/loans' },
      { label: 'Document Requests', icon: 'pi pi-fw pi-file', route: '/me/documents' },
      { label: 'Announcements', icon: 'pi pi-fw pi-megaphone', route: '/me/announcements' },
    ];
    const taskItems: NavItem[] = [
      { label: 'My Approvals', icon: 'pi pi-fw pi-check-square', route: '/me/approvals/my-approvals' },
    ];

    if (compact) {
      // Keep self-service under My Portal, but My Approvals stays under My Tasks
      // (same as pure Employee) so approvers/managers find it in one place.
      return [
        {
          label: EMPLOYEE_PORTAL_PANEL_LABEL,
          icon: PANEL_ICONS[EMPLOYEE_PORTAL_PANEL_LABEL],
          items: menuItems,
        },
        { label: 'My Tasks', icon: PANEL_ICONS['My Tasks'], items: taskItems },
      ];
    }

    return [
      { label: 'My Menu', icon: PANEL_ICONS['My Menu'], items: menuItems },
      { label: 'My Tasks', icon: PANEL_ICONS['My Tasks'], items: taskItems },
    ];
  }

  private buildMenu(roleFeatures: any[]): void {
    if (!this.userProfileStore.hasProfile()) return;

    if (!roleFeatures || roleFeatures.length === 0) {
      // Non-employee roles with no CanView grants still keep employee self-service
      // when linked to an employee record (common for provisioned tenant admins),
      // unless the plan excludes the self-service portal (Starter).
      if (this.userProfileStore.employeeGuid() && this.userProfileStore.hasEmployeeSelfService()) {
        this.navMenu = [...this.employeePortalSections(true)];
      } else {
        this.navMenu = [];
      }
      return;
    }

    const featuresWithPanel = roleFeatures.filter((f) => f.groupPanel && f.routeUrl);
    const groupedByPanel = new Map<string, any[]>();

    featuresWithPanel.forEach((feature) => {
      if (!groupedByPanel.has(feature.groupPanel)) {
        groupedByPanel.set(feature.groupPanel, []);
      }
      groupedByPanel.get(feature.groupPanel)!.push(feature);
    });

    const menuItems: NavItem[] = [];

    Array.from(groupedByPanel.entries())
      .sort((a, b) => {
        const panelAId = a[1][0]?.groupPanelId || 0;
        const panelBId = b[1][0]?.groupPanelId || 0;
        return panelAId - panelBId;
      })
      .forEach(([panelName, panelFeatures]) => {
        const panelItem: NavItem = {
          label: panelName,
          icon: this.panelIconForLabel(panelName),
          items: [],
        };

        const groupedByFeature = new Map<string, any[]>();
        const withoutGroupFeature: any[] = [];

        panelFeatures.forEach((feature) => {
          if (feature.groupFeature) {
            if (!groupedByFeature.has(feature.groupFeature)) {
              groupedByFeature.set(feature.groupFeature, []);
            }
            groupedByFeature.get(feature.groupFeature)!.push(feature);
          } else {
            withoutGroupFeature.push(feature);
          }
        });

        Array.from(groupedByFeature.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .forEach(([groupFeatureName, features]) => {
            const navFeatures = features
              .filter((f) => !this.isDuplicateDashboardFeature(f))
              .sort((a, b) => a.featureName.localeCompare(b.featureName));

            if (navFeatures.length === 0) {
              return;
            }

            panelItem.items!.push({
              label: groupFeatureName,
              icon: 'pi pi-fw pi-list',
              items: navFeatures.map((f) => this.mapFeatureNavItem(f)),
            });
          });

        withoutGroupFeature
          .filter((f) => !this.isDuplicateDashboardFeature(f))
          .sort((a, b) => a.featureName.localeCompare(b.featureName))
          .forEach((f) => {
            panelItem.items!.push(this.mapFeatureNavItem(f));
          });

        if (panelItem.items!.length > 0) {
          menuItems.push(panelItem);
        }
      });

    const dashboardSection = this.dashboardsSection(roleFeatures);
    const sections: NavItem[] = [];

    if (dashboardSection) {
      sections.push(dashboardSection);
    }

    if (this.userProfileStore.employeeGuid() && this.userProfileStore.hasEmployeeSelfService()) {
      sections.push(...this.employeePortalSections(true));
    }

    sections.push(...menuItems);
    this.navMenu = sections;
  }

  private dashboardsSection(roleFeatures: any[]): NavItem | null {
    const panels = new Set(
      roleFeatures
        .filter((f) => f.groupPanel)
        .map((f) => String(f.groupPanel).trim().toLowerCase()),
    );

    const items: NavItem[] = [];

    if (panels.has('payroll')) {
      items.push({
        label: 'Payroll Dashboard',
        icon: 'pi pi-fw pi-wallet',
        route: '/features/payroll',
      });
    }

    if (panels.has('hr')) {
      items.push({
        label: 'HR Dashboard',
        icon: 'pi pi-fw pi-users',
        route: '/features/hr/dashboard',
      });
    }

    if (items.length === 0) {
      return null;
    }

    return {
      label: DASHBOARDS_PANEL_LABEL,
      icon: PANEL_ICONS[DASHBOARDS_PANEL_LABEL],
      items,
    };
  }

  private isDuplicateDashboardFeature(feature: any): boolean {
    const name = String(feature.featureName ?? '').trim().toLowerCase();
    const route = String(feature.routeUrl ?? '').split('?')[0];

    if (name === 'dashboard') {
      return true;
    }

    return DASHBOARD_FEATURE_ROUTES.has(route);
  }

  private mapFeatureNavItem(feature: any): NavItem {
    return {
      label: feature.featureName,
      icon: feature.iconClass || 'pi pi-fw pi-file',
      route: feature.routeUrl,
    };
  }

  private syncExpandedFromRoute(url: string = this.router.url): void {
    const path = url.split('?')[0];
    const match = this.findBestRouteMatch(path);

    this.expandedPanels.clear();
    this.expandedGroups.clear();

    // No matching route → leave everything collapsed (lighter default).
    if (!match) {
      return;
    }

    this.expandedPanels.add(match.panel.label);
    if (match.group) {
      this.expandedGroups.add(this.groupKey(match.panel.label, match.group.label));
    }
  }

  /**
   * Prefer the longest matching nav route so a dashboard like `/features/payroll`
   * does not steal expansion from a child module such as `/features/payroll/settings`.
   */
  private findBestRouteMatch(url: string): { panel: NavItem; group?: NavItem } | null {
    let best: { panel: NavItem; group?: NavItem; score: number } | null = null;

    const consider = (panel: NavItem, group: NavItem | undefined, navRoute?: string): void => {
      if (!navRoute) {
        return;
      }
      const score = this.routeMatchScore(navRoute, url);
      if (score > 0 && (!best || score > best.score)) {
        best = { panel, group, score };
      }
    };

    for (const panel of this.navMenu) {
      for (const item of panel.items ?? []) {
        consider(panel, undefined, item.route);
        for (const sub of item.items ?? []) {
          consider(panel, item, sub.route);
        }
      }
    }

    return best;
  }

  private routeMatchScore(navRoute: string, url: string): number {
    const route = navRoute.split('?')[0];
    if (url === route) {
      return route.length + 1;
    }
    if (url.startsWith(`${route}/`)) {
      return route.length;
    }
    return 0;
  }

  private countNavItems(menu: NavItem[]): number {
    let count = 0;
    for (const panel of menu) {
      for (const item of panel.items ?? []) {
        if (item.items?.length) {
          count += item.items.length;
        } else {
          count += 1;
        }
      }
    }
    return count;
  }

  private filterNavMenu(menu: NavItem[], query: string): NavItem[] {
    const filtered: NavItem[] = [];

    for (const panel of menu) {
      const filteredItems = (panel.items ?? []).flatMap((item) => {
        if (item.items?.length) {
          const matchingChildren = item.items.filter((child) =>
            child.label.toLowerCase().includes(query),
          );
          if (matchingChildren.length > 0) {
            return [{ ...item, items: matchingChildren }];
          }
          if (item.label.toLowerCase().includes(query)) {
            return [item];
          }
          return [];
        }

        return item.label.toLowerCase().includes(query) ? [item] : [];
      });

      if (filteredItems.length > 0) {
        filtered.push({ ...panel, items: filteredItems });
      }
    }

    return filtered;
  }
}
